import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../api';

function AdminPanel() {
  const [tab, setTab] = useState('dashboard');
  const [msg, setMsg] = useState(null);
  const [msgType, setMsgType] = useState('info');

  // Dashboard
  const [stats, setStats] = useState(null);

  // Players
  const [players, setPlayers] = useState([]);
  const [playerSearch, setPlayerSearch] = useState('');
  const [playerPage, setPlayerPage] = useState(1);
  const [playerTotal, setPlayerTotal] = useState(0);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  // Grant form
  const [grantCredits, setGrantCredits] = useState('');
  const [grantMinerals, setGrantMinerals] = useState('');
  const [grantRare, setGrantRare] = useState('');
  const [grantEnergy, setGrantEnergy] = useState('');

  // Ghosts
  const [ghosts, setGhosts] = useState([]);

  // Guide articles
  const [guideArticles, setGuideArticles] = useState([]);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({ title: '', slug: '', category: 'general', content: '', sort_order: 0, is_published: true });

  const showMsg = (text, type = 'info') => {
    setMsg(text);
    setMsgType(type);
    setTimeout(() => setMsg(null), 4000);
  };

  // Dashboard
  const loadDashboard = useCallback(async () => {
    try {
      const { data } = await api.getAdminDashboard();
      setStats(data);
    } catch (err) { showMsg('Failed to load dashboard', 'error'); }
  }, []);

  // Players
  const loadPlayers = useCallback(async () => {
    try {
      const { data } = await api.getAdminPlayers({ page: playerPage, search: playerSearch || undefined });
      setPlayers(data.players || []);
      setPlayerTotal(data.total || 0);
    } catch (err) { showMsg('Failed to load players', 'error'); }
  }, [playerPage, playerSearch]);

  // Ghosts
  const loadGhosts = useCallback(async () => {
    try {
      const { data } = await api.getAdminGhosts();
      setGhosts(data.ghosts || []);
    } catch (err) { showMsg('Failed to load ghosts', 'error'); }
  }, []);

  // Guide
  const loadGuide = useCallback(async () => {
    try {
      const { data } = await api.getAdminGuideArticles();
      setGuideArticles(data.articles || []);
    } catch (err) { showMsg('Failed to load guide articles', 'error'); }
  }, []);

  useEffect(() => {
    if (tab === 'dashboard') loadDashboard();
    if (tab === 'players') loadPlayers();
    if (tab === 'ghosts') loadGhosts();
    if (tab === 'guide') loadGuide();
  }, [tab, loadDashboard, loadPlayers, loadGhosts, loadGuide]);

  const handleViewPlayer = async (id) => {
    try {
      const { data } = await api.getAdminPlayerDetail(id);
      setSelectedPlayer(data);
    } catch (err) { showMsg('Failed to load player details', 'error'); }
  };

  const handleBan = async (id) => {
    const reason = window.prompt('Ban reason (optional):');
    if (reason === null) return;
    try {
      await api.banPlayer(id, reason || 'Banned by admin');
      showMsg('Player banned', 'success');
      await loadPlayers();
      if (selectedPlayer?.user?.id === id) await handleViewPlayer(id);
    } catch (err) { showMsg(err.response?.data?.error || 'Ban failed', 'error'); }
  };

  const handleUnban = async (id) => {
    try {
      await api.unbanPlayer(id);
      showMsg('Player unbanned', 'success');
      await loadPlayers();
      if (selectedPlayer?.user?.id === id) await handleViewPlayer(id);
    } catch (err) { showMsg(err.response?.data?.error || 'Unban failed', 'error'); }
  };

  const handleGrant = async (id) => {
    const resources = {};
    if (grantCredits) resources.credits = Number(grantCredits);
    if (grantMinerals) resources.common_minerals = Number(grantMinerals);
    if (grantRare) resources.rare_elements = Number(grantRare);
    if (grantEnergy) resources.energy = Number(grantEnergy);
    if (Object.keys(resources).length === 0) return showMsg('Enter at least one resource amount', 'error');
    try {
      await api.grantResources(id, resources);
      showMsg('Resources granted', 'success');
      setGrantCredits(''); setGrantMinerals(''); setGrantRare(''); setGrantEnergy('');
      if (selectedPlayer?.user?.id === id) await handleViewPlayer(id);
    } catch (err) { showMsg(err.response?.data?.error || 'Grant failed', 'error'); }
  };

  const handleReset = async (id) => {
    if (!window.confirm('⚠ This will delete ALL player data (ships, drones, stations, missions) and reset inventory to defaults. Are you sure?')) return;
    try {
      await api.resetPlayer(id);
      showMsg('Player reset to starter state', 'success');
      if (selectedPlayer?.user?.id === id) await handleViewPlayer(id);
    } catch (err) { showMsg(err.response?.data?.error || 'Reset failed', 'error'); }
  };

  // Guide CRUD
  const handleSaveArticle = async () => {
    try {
      if (editingArticle) {
        await api.updateGuideArticle(editingArticle.id, articleForm);
        showMsg('Article updated', 'success');
      } else {
        await api.createGuideArticle(articleForm);
        showMsg('Article created', 'success');
      }
      setEditingArticle(null);
      setArticleForm({ title: '', slug: '', category: 'general', content: '', sort_order: 0, is_published: true });
      await loadGuide();
    } catch (err) { showMsg(err.response?.data?.error || 'Save failed', 'error'); }
  };

  const handleEditArticle = (a) => {
    setEditingArticle(a);
    setArticleForm({ title: a.title, slug: a.slug, category: a.category, content: a.content, sort_order: a.sort_order, is_published: a.is_published });
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm('Delete this guide article?')) return;
    try {
      await api.deleteGuideArticle(id);
      showMsg('Article deleted', 'success');
      await loadGuide();
    } catch (err) { showMsg(err.response?.data?.error || 'Delete failed', 'error'); }
  };

  const fmtNum = (n) => Number(n || 0).toLocaleString();

  return (
    <div className="panel admin-panel">
      {msg && <div className={`hud-toast hud-toast-${msgType}`}>{msg}</div>}

      <div className="market-tabs">
        <button className={`tab-btn ${tab === 'dashboard' ? 'active' : ''}`} onClick={() => setTab('dashboard')}>
          DASHBOARD
        </button>
        <button className={`tab-btn ${tab === 'players' ? 'active' : ''}`} onClick={() => setTab('players')}>
          PLAYERS
        </button>
        <button className={`tab-btn ${tab === 'ghosts' ? 'active' : ''}`} onClick={() => setTab('ghosts')}>
          GHOSTS
        </button>
        <button className={`tab-btn ${tab === 'guide' ? 'active' : ''}`} onClick={() => setTab('guide')}>
          GUIDE
        </button>
      </div>

      {/* Dashboard */}
      {tab === 'dashboard' && stats && (
        <section className="section">
          <h3 className="section-title"><span className="title-accent">📊</span> SERVER OVERVIEW</h3>
          <div className="admin-stats-grid">
            <div className="admin-stat-card">
              <div className="admin-stat-num">{fmtNum(stats.totalPlayers)}</div>
              <div className="admin-stat-label">Players</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-num">{fmtNum(stats.activePlayers)}</div>
              <div className="admin-stat-label">Active (24h)</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-num">{fmtNum(stats.totalShips)}</div>
              <div className="admin-stat-label">Ships</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-num">{fmtNum(stats.totalDrones)}</div>
              <div className="admin-stat-label">Drones</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-num">{fmtNum(stats.totalStations)}</div>
              <div className="admin-stat-label">Stations</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-num">{fmtNum(stats.activeMissions)}</div>
              <div className="admin-stat-label">Active Missions</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-num">{fmtNum(stats.activeListings)}</div>
              <div className="admin-stat-label">Market Listings</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-num">{fmtNum(stats.totalTransactions)}</div>
              <div className="admin-stat-label">Total Trades</div>
            </div>
            <div className="admin-stat-card">
              <div className="admin-stat-num">{fmtNum(stats.totalGhosts)}</div>
              <div className="admin-stat-label">Ghost Players</div>
            </div>
          </div>
        </section>
      )}

      {/* Players */}
      {tab === 'players' && (
        <section className="section">
          <h3 className="section-title"><span className="title-accent">👥</span> PLAYER MANAGEMENT</h3>
          <div className="admin-search-bar">
            <input
              className="hud-input"
              type="text"
              placeholder="Search by username..."
              value={playerSearch}
              onChange={e => { setPlayerSearch(e.target.value); setPlayerPage(1); }}
            />
            <span className="admin-total">{playerTotal} players</span>
          </div>

          <div className="admin-players-layout">
            {/* Player list */}
            <div className="admin-player-list">
              <div className="hud-table-wrap">
                <table className="hud-table">
                  <thead>
                    <tr>
                      <th>USERNAME</th>
                      <th>CREDITS</th>
                      <th>STATUS</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {players.map(p => (
                      <tr key={p.id} className={selectedPlayer?.user?.id === p.id ? 'row-selected' : ''}>
                        <td>{p.display_name || p.username}</td>
                        <td>{fmtNum(p.inventory?.credits)}</td>
                        <td>
                          {p.is_banned
                            ? <span className="status-badge status-banned">BANNED</span>
                            : <span className="status-badge status-active">ACTIVE</span>}
                        </td>
                        <td>
                          <button className="btn btn-sm btn-ghost" onClick={() => handleViewPlayer(p.id)}>VIEW</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {playerTotal > 20 && (
                <div className="admin-pagination">
                  <button className="btn btn-sm" disabled={playerPage <= 1} onClick={() => setPlayerPage(p => p - 1)}>← PREV</button>
                  <span className="page-info">Page {playerPage} / {Math.ceil(playerTotal / 20)}</span>
                  <button className="btn btn-sm" disabled={playerPage >= Math.ceil(playerTotal / 20)} onClick={() => setPlayerPage(p => p + 1)}>NEXT →</button>
                </div>
              )}
            </div>

            {/* Player detail */}
            {selectedPlayer && (
              <div className="admin-player-detail">
                <div className="hud-card">
                  <h4 className="card-name">{selectedPlayer.user.display_name || selectedPlayer.user.username}</h4>
                  <div className="admin-detail-grid">
                    <div className="admin-detail-item">
                      <span className="stat-label">ID</span>
                      <span className="stat-value">{selectedPlayer.user.id}</span>
                    </div>
                    <div className="admin-detail-item">
                      <span className="stat-label">USERNAME</span>
                      <span className="stat-value">{selectedPlayer.user.username}</span>
                    </div>
                    <div className="admin-detail-item">
                      <span className="stat-label">STATUS</span>
                      <span className="stat-value">
                        {selectedPlayer.user.is_banned
                          ? <span className="status-badge status-banned">BANNED</span>
                          : <span className="status-badge status-active">ACTIVE</span>}
                      </span>
                    </div>
                    {selectedPlayer.user.inventory && (
                      <>
                        <div className="admin-detail-item">
                          <span className="stat-label">CREDITS</span>
                          <span className="stat-value">{fmtNum(selectedPlayer.user.inventory.credits)}</span>
                        </div>
                        <div className="admin-detail-item">
                          <span className="stat-label">MINERALS</span>
                          <span className="stat-value">{fmtNum(selectedPlayer.user.inventory.common_minerals)}</span>
                        </div>
                        <div className="admin-detail-item">
                          <span className="stat-label">RARE</span>
                          <span className="stat-value">{fmtNum(selectedPlayer.user.inventory.rare_elements)}</span>
                        </div>
                        <div className="admin-detail-item">
                          <span className="stat-label">ENERGY</span>
                          <span className="stat-value">{fmtNum(selectedPlayer.user.inventory.energy)}</span>
                        </div>
                      </>
                    )}
                    <div className="admin-detail-item">
                      <span className="stat-label">SHIPS</span>
                      <span className="stat-value">{selectedPlayer.ships?.length || 0}</span>
                    </div>
                    <div className="admin-detail-item">
                      <span className="stat-label">DRONES</span>
                      <span className="stat-value">{selectedPlayer.drones?.length || 0}</span>
                    </div>
                    <div className="admin-detail-item">
                      <span className="stat-label">STATIONS</span>
                      <span className="stat-value">{selectedPlayer.stations?.length || 0}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="admin-actions">
                    {selectedPlayer.user.is_banned ? (
                      <button className="btn btn-sm btn-success" onClick={() => handleUnban(selectedPlayer.user.id)}>UNBAN</button>
                    ) : (
                      <button className="btn btn-sm btn-danger" onClick={() => handleBan(selectedPlayer.user.id)}>BAN</button>
                    )}
                    <button className="btn btn-sm btn-danger" onClick={() => handleReset(selectedPlayer.user.id)}>RESET</button>
                  </div>

                  {/* Grant resources */}
                  <div className="admin-grant-section">
                    <h5 className="grant-heading">GRANT RESOURCES</h5>
                    <div className="grant-form-grid">
                      <div className="form-group">
                        <label className="hud-label">Credits</label>
                        <input className="hud-input" type="number" placeholder="0" value={grantCredits} onChange={e => setGrantCredits(e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label className="hud-label">Minerals</label>
                        <input className="hud-input" type="number" placeholder="0" value={grantMinerals} onChange={e => setGrantMinerals(e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label className="hud-label">Rare</label>
                        <input className="hud-input" type="number" placeholder="0" value={grantRare} onChange={e => setGrantRare(e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label className="hud-label">Energy</label>
                        <input className="hud-input" type="number" placeholder="0" value={grantEnergy} onChange={e => setGrantEnergy(e.target.value)} />
                      </div>
                    </div>
                    <button className="btn btn-glow" onClick={() => handleGrant(selectedPlayer.user.id)}>GRANT</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Ghosts */}
      {tab === 'ghosts' && (
        <section className="section">
          <h3 className="section-title"><span className="title-accent">👻</span> GHOST PLAYERS (AI)</h3>
          <p className="section-desc">Ghost players are AI-controlled accounts used for market activity and leaderboard population.</p>
          {ghosts.length === 0 && <p className="hud-empty">No ghost players found.</p>}
          {ghosts.length > 0 && (
            <div className="hud-table-wrap">
              <table className="hud-table">
                <thead>
                  <tr>
                    <th>NAME</th>
                    <th>WEALTH</th>
                    <th>FLEET POWER</th>
                    <th>MARKET PROFIT</th>
                    <th>GROWTH</th>
                    <th>STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  {ghosts.map(g => (
                    <tr key={g.id}>
                      <td>{g.name}</td>
                      <td>{fmtNum(g.wealth)}</td>
                      <td>{fmtNum(g.fleet_power)}</td>
                      <td>{fmtNum(g.market_profit)}</td>
                      <td>{Number(g.growth_rate).toFixed(3)}x</td>
                      <td><span className="status-badge status-ghost">GHOST</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Guide */}
      {tab === 'guide' && (
        <section className="section">
          <h3 className="section-title"><span className="title-accent">📖</span> GUIDE ARTICLES</h3>
          <p className="section-desc">Create and edit game guide/wiki articles. Players can view published articles in the Guide tab.</p>

          {/* Article form */}
          <div className="hud-card" style={{ marginBottom: '1rem' }}>
            <h4 className="card-name">{editingArticle ? `Edit: ${editingArticle.title}` : 'New Article'}</h4>
            <div className="form-row" style={{ marginBottom: '0.75rem' }}>
              <div className="form-group">
                <label className="hud-label">Title</label>
                <input className="hud-input" type="text" placeholder="Article title" value={articleForm.title}
                  onChange={e => setArticleForm(prev => ({ ...prev, title: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="hud-label">Slug (URL)</label>
                <input className="hud-input" type="text" placeholder="article-slug" value={articleForm.slug}
                  onChange={e => setArticleForm(prev => ({ ...prev, slug: e.target.value }))} />
              </div>
            </div>
            <div className="form-row" style={{ marginBottom: '0.75rem' }}>
              <div className="form-group">
                <label className="hud-label">Category</label>
                <select className="hud-select" value={articleForm.category}
                  onChange={e => setArticleForm(prev => ({ ...prev, category: e.target.value }))}>
                  <option value="getting-started">Getting Started</option>
                  <option value="resources">Resources & Economy</option>
                  <option value="fleet">Ships & Fleet</option>
                  <option value="stations">Stations</option>
                  <option value="combat">Combat</option>
                  <option value="sectors">Sectors & Exploration</option>
                  <option value="production">Production & Crafting</option>
                  <option value="market">Market & Trade</option>
                  <option value="corporations">Corporations</option>
                  <option value="general">General</option>
                </select>
              </div>
              <div className="form-group">
                <label className="hud-label">Sort Order</label>
                <input className="hud-input" type="number" value={articleForm.sort_order}
                  onChange={e => setArticleForm(prev => ({ ...prev, sort_order: Number(e.target.value) }))} />
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={articleForm.is_published}
                    onChange={e => setArticleForm(prev => ({ ...prev, is_published: e.target.checked }))} />
                  <span className="hud-label" style={{ margin: 0 }}>Published</span>
                </label>
              </div>
            </div>
            <div className="form-group" style={{ marginBottom: '0.75rem' }}>
              <label className="hud-label">Content (Markdown-ish: # headings, **bold**, *italic*, - lists, `code`)</label>
              <textarea className="hud-input" rows={10} placeholder="Article content..."
                value={articleForm.content} style={{ resize: 'vertical', minHeight: '150px' }}
                onChange={e => setArticleForm(prev => ({ ...prev, content: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-glow" onClick={handleSaveArticle}>
                {editingArticle ? 'UPDATE' : 'CREATE'}
              </button>
              {editingArticle && (
                <button className="btn btn-ghost" onClick={() => {
                  setEditingArticle(null);
                  setArticleForm({ title: '', slug: '', category: 'general', content: '', sort_order: 0, is_published: true });
                }}>CANCEL</button>
              )}
            </div>
          </div>

          {/* Article list */}
          {guideArticles.length > 0 && (
            <div className="hud-table-wrap">
              <table className="hud-table">
                <thead>
                  <tr>
                    <th>TITLE</th>
                    <th>SLUG</th>
                    <th>CATEGORY</th>
                    <th>ORDER</th>
                    <th>STATUS</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {guideArticles.map(a => (
                    <tr key={a.id}>
                      <td>{a.title}</td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem' }}>{a.slug}</td>
                      <td>{a.category}</td>
                      <td>{a.sort_order}</td>
                      <td>
                        {a.is_published
                          ? <span className="status-badge status-active">LIVE</span>
                          : <span className="status-badge" style={{ background: 'rgba(100,100,100,0.2)', color: '#888' }}>DRAFT</span>}
                      </td>
                      <td style={{ display: 'flex', gap: '0.3rem' }}>
                        <button className="btn btn-sm btn-ghost" onClick={() => handleEditArticle(a)}>EDIT</button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDeleteArticle(a.id)}>DEL</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default AdminPanel;
